import { HttpClient } from '@angular/common/http';
import { ApiUrl, IrisBaseCRUDService, IrisQueryParams, IrisQueryParamsBuilder } from '@iris/api-query';
import { IrisWorkflowI } from '@iris/common/models/IrisWorkflow';
import { IrisWorkflowStateAllowedUsersI, IrisWorkflowStateI, IrisWorkflowStatesOrder } from '@iris/common/models/IrisWorkflowState';
import { Observable } from 'rxjs';
import { inject, InjectionToken } from '@angular/core';
import { switchMap, take, tap } from 'rxjs/operators';
import { MatDialog } from '@angular/material/dialog';
import { IrisModuleWorkflowStateDiagramComponent, IrisModuleWorkflowStateDiagramComponentData } from '@iris/common/modules/global-settings/workflow-global-settings/components/module-workflow-state-diagram/module-workflow-state-diagram.component';
import { IrisPage } from '@iris/common/models/page';
import { IrisUserGroupI } from '@iris/common/modules/user-common/models/IrisUserGroup';

export interface WorkflowApiService extends IrisBaseCRUDService<IrisWorkflowI> {
  getAllWorkflowsByType(type: string): Observable<IrisWorkflowI[]>;
  createWorkflow(workflow: IrisWorkflowI): Observable<IrisWorkflowI>;
  updateWorkflow(workflow: IrisWorkflowI): Observable<IrisWorkflowI>;
  getWorkflow(workflowId: number, params?: IrisQueryParams): Observable<IrisWorkflowI>;
  copyWorkflow(workflowId: number, workflow: Partial<IrisWorkflowI>): Observable<IrisWorkflowI>;
  removeWorkflow(workflowId: number): Observable<IrisWorkflowI>;

  getWorkflowStates(workflowId: number, params?: IrisQueryParams): Observable<IrisWorkflowStateI[]>;
  getWorkflowState(workflowId: number, workflowStateId: number, params?: IrisQueryParams): Observable<IrisWorkflowStateI>;
  getWorkflowStateUserGroups(workflowId: number, alias: string, params?: IrisQueryParams): Observable<IrisPage<IrisUserGroupI>>;
  getWorkflowStateAllowedUsers(workflowId: number, workflowStateAlias: string, params?: IrisQueryParams): Observable<IrisWorkflowStateAllowedUsersI>;
  createWorkflowState(workflowId: number, workflowState: Partial<IrisWorkflowStateI>): Observable<IrisWorkflowStateI>;
  saveWorkflowState(workflowState: IrisWorkflowStateI): Observable<IrisWorkflowStateI>;
  setWorkflowStateOrder(workflowId: number, workflowStatesOrder: IrisWorkflowStatesOrder[]): Observable<IrisWorkflowStatesOrder[]>;
  removeWorkflowState(workflowId: number, workflowStateId: number, params?: IrisQueryParams): Observable<IrisWorkflowStateI>;

  showWorkflowDiagram(workflowId: number, canSave?: boolean): void;
}

export const WORKFLOW_API_SERVICE = new InjectionToken<WorkflowApiService>('Workflow API service');

export abstract class WorkflowApiServiceBase extends IrisBaseCRUDService<IrisWorkflowI> implements WorkflowApiService {
  protected readonly dialog = inject(MatDialog);

  protected constructor(
    httpClient: HttpClient,
  ) {
    super(httpClient);
  }

  getAllWorkflowsByType(type: string, params: IrisQueryParams = new IrisQueryParams()): Observable<IrisWorkflowI[]> {
    const queryParams = new IrisQueryParamsBuilder(params)
      .filter('type', [type])
      .toStructure();
    return this.getAll(queryParams);
  }

  createWorkflow(workflow: IrisWorkflowI): Observable<IrisWorkflowI> {
    return this.httpClient.post<IrisWorkflowI>(this.url(), workflow);
  }

  @ApiUrl('~/{workflowId}')
  getWorkflow(workflowId: number, params: IrisQueryParams = new IrisQueryParams()): Observable<IrisWorkflowI> {
    return this.httpClient.get<IrisWorkflowI>(this.url({ workflowId }), { params: params.toObject() });
  }

  @ApiUrl('~/{workflowId}/copy')
  copyWorkflow(workflowId: number, workflow: IrisWorkflowI): Observable<IrisWorkflowI> {
    return this.httpClient.post<IrisWorkflowI>(this.url({ workflowId }), workflow);
  }

  @ApiUrl('~/{workflowId}')
  updateWorkflow(workflow: IrisWorkflowI): Observable<IrisWorkflowI> {
    return this.httpClient.post<IrisWorkflowI>(this.url({ workflowId: workflow.id }), workflow);
  }

  @ApiUrl('~/{workflowId}')
  removeWorkflow(workflowId: number): Observable<IrisWorkflowI> {
    return this.httpClient.delete<IrisWorkflowI>(this.url({ workflowId }));
  }

  @ApiUrl('~/{workflowId}/states')
  getWorkflowStates(workflowId: number, params: IrisQueryParams = new IrisQueryParams()): Observable<IrisWorkflowStateI[]> {
    return this.httpClient.get<IrisWorkflowStateI[]>(this.url({ workflowId }), { params: params.toObject() });
  }

  @ApiUrl('~/{workflowId}/states/{workflowStateId}')
  getWorkflowState(workflowId: number, workflowStateId: number, params: IrisQueryParams = new IrisQueryParams()): Observable<IrisWorkflowStateI> {
    return this.httpClient.get<IrisWorkflowStateI>(this.url({ workflowId, workflowStateId }), { params: params.toObject() });
  }

  @ApiUrl('~/{workflowId}/states/{alias}/user-groups')
  getWorkflowStateUserGroups(workflowId: number, alias: string, params: IrisQueryParams = new IrisQueryParams()): Observable<IrisPage<IrisUserGroupI>> {
    return this.httpClient.get<IrisPage<IrisUserGroupI>>(this.url({ workflowId, alias }), { params: params.toObject() });
  }

  @ApiUrl('~/{workflowId}/states/{workflowStateAlias}/users')
  getWorkflowStateAllowedUsers(workflowId: number, workflowStateAlias: string, params: IrisQueryParams = new IrisQueryParams()): Observable<IrisWorkflowStateAllowedUsersI> {
    return this.httpClient.get<IrisWorkflowStateAllowedUsersI>(this.url({ workflowId, workflowStateAlias }), { params: params.toObject() });
  }

  @ApiUrl('~/{workflowId}/states')
  createWorkflowState(workflowId: number, workflowState: IrisWorkflowStateI): Observable<IrisWorkflowStateI> {
    return this.httpClient.post<IrisWorkflowStateI>(this.url({ workflowId }), workflowState).pipe(
      tap(res => this.onWorkflowStateChanged(workflowId, res?.id ?? workflowState?.id ?? null)),
    );
  }

  @ApiUrl('~/{workflowId}/states/{workflowStateId}')
  saveWorkflowState(workflowState: IrisWorkflowStateI): Observable<IrisWorkflowStateI> {
    return this.httpClient.post<IrisWorkflowStateI>(this.url({ workflowStateId: workflowState.id, workflowId: workflowState.workflowId }), workflowState ).pipe(
      tap(res => this.onWorkflowStateChanged(workflowState.workflowId, res?.id ?? workflowState?.id ?? null)),
    );
  }

  @ApiUrl('~/{workflowId}/states/set-order')
  setWorkflowStateOrder(workflowId: number, workflowStatesOrder: IrisWorkflowStatesOrder[]): Observable<IrisWorkflowStatesOrder[]> {
    return this.httpClient.post<IrisWorkflowStatesOrder[]>(this.url({ workflowId }), workflowStatesOrder).pipe(
      tap(() => this.onWorkflowStateChanged(workflowId, null)),
    );
  }

  @ApiUrl('~/{workflowId}/states/{workflowStateId}')
  removeWorkflowState(workflowId: number, workflowStateId: number, params?: IrisQueryParams): Observable<IrisWorkflowStateI> {
    return this.httpClient.delete<IrisWorkflowStateI>(this.url({ workflowId, workflowStateId }), { params: params?.toObject() }).pipe(
      tap(() => this.onWorkflowStateChanged(workflowId, workflowStateId)),
    );
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  protected onWorkflowStateChanged(workflowId: IrisWorkflowI['id'], workflowStateId: IrisWorkflowStateI['id'] | null): void {
    // no-op
  }

  showWorkflowDiagram(workflowId: number, canSave = false): void {
    this.getById(workflowId).pipe(
      switchMap(workflow => {
        const dialogRef = this.dialog.open<IrisModuleWorkflowStateDiagramComponent, IrisModuleWorkflowStateDiagramComponentData>(IrisModuleWorkflowStateDiagramComponent,{
          data: {
            workflow,
            canSave,
          },
        });

        return dialogRef.componentInstance.saveRequested.pipe(
          take(1),
          switchMap(value => this.update(workflowId, value)),
          tap(() => dialogRef.close()),
        );
      }),
    ).subscribe();
  }
}
